import * as React from 'react';
import Button from '../../../../common/form/Button';
import { useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import Row from 'reactstrap/lib/Row';
import Container from 'reactstrap/lib/Container';
import Col from 'reactstrap/lib/Col';
import FormInput from '../../../../common/form/Input';
import {
    IgloohomeLock,
    getIgloohomeLocks,
    IgloohomePropertyConfiguration,
    createIgloohomePropertyConfiguration,
    deleteIgloohomeLock,
    IgloohomeLockFromIgloohomeService,
    addIgloohomeLock,
    deleteIgloohomePropertyConfiguration
} from '../../../../../api/PropertyIntegration';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import FormValidationDisplay from '../../../../common/form/ValidationDisplay';
import { ApiResult } from '../../../../../api/Shared';
import IgloohomeLockRow from './IgloohomeLockRow';
import IgloohomeSelectLockRow from './IgloohomeSelectLockRow';
import { userIsCompanyManageUser, userIsPropertyManageUser, userIsSystemManageUser } from '../../../../../utilities/identity';

interface Props {
    igloohomeConfiguration?: IgloohomePropertyConfiguration;
    propertyId: number;
    reload: () => Promise<void>;
    update: (newConfig?: IgloohomePropertyConfiguration) => void;
    setIsLoading: (isLoading: boolean) => void;
}

export const IgloohomeLocksModal = (props: Props) => {
    const isUser =
    userIsCompanyManageUser() ||
    userIsPropertyManageUser() ||
    userIsSystemManageUser();
    const [isOpen, setIsOpen] = useState(false);
    const [apiKey, setApiKey] = useState<string>('');
    const [error, setError] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [locksFromIgloohomeService, setLocksFromIgloohomeService] = useState<
        IgloohomeLockFromIgloohomeService[] | undefined
    >(undefined);

    const configExists = props.igloohomeConfiguration !== null;
    const locksFromIgloohomeConfiguration =
        props.igloohomeConfiguration === null || props.igloohomeConfiguration === undefined
            ? ([] as IgloohomeLock[])
            : props.igloohomeConfiguration.igloohomeLocks;

    const closePopup = () => {
        if (!loading) {
            setIsOpen(false);
            setError(undefined);
            setLocksFromIgloohomeService(undefined);
            setLoading(false);
        }
    };

    function callApiAction<T>(
        apiFunction: () => Promise<ApiResult<T>>,
        successFunction: (data?: T) => void,
        errorDefault: string
    ) {
        return async () => {
            setError(undefined);
            setLoading(true);
            let result = await apiFunction();
            setLoading(false);
            if (result.success) {
                successFunction(result.data);
            } else {
                setError(result.error || errorDefault);
            }
        };
    }

    const handleLockDelete = async (lockToDelete: IgloohomeLock) => {
        if (!locksFromIgloohomeConfiguration) {
            return;
        }

        await callApiAction(
            () => deleteIgloohomeLock(props.propertyId, lockToDelete.lockUniqueId),
            () =>
                props.update({
                    apiKey,
                    igloohomeLocks: locksFromIgloohomeConfiguration.filter(
                        lock => lock !== lockToDelete
                    )
                }),
            'Failed to delete lock'
        )();
    };

    const handleLockAdd = async (lockUniqueId: string, lockName: string, displayName: string) => {
        await callApiAction(
            () => addIgloohomeLock(props.propertyId, { lockName, lockUniqueId, displayName }),
            () =>
                props.update({
                    apiKey,
                    igloohomeLocks: locksFromIgloohomeConfiguration.concat({
                        lockName,
                        lockUniqueId,
                        displayName
                    })
                }),
            'Failed to add lock'
        )();
    };

    const loadLocksFromIgloohomeService = async (apiKeyToLoad: string) => {
        await callApiAction(
            () => getIgloohomeLocks(apiKeyToLoad),
            data => setLocksFromIgloohomeService(data),
            'Failed to load locks'
        )();
    };

    const addIgloohomeConfiguration = async () => {
        if (!apiKey) {
            setError('No API Key Entered');
            return;
        }

        setLoading(true);
        let result = await createIgloohomePropertyConfiguration(props.propertyId, apiKey);
        setLoading(false);
        if (result.success) {
            props.update({ apiKey, igloohomeLocks: [] });
            await loadLocksFromIgloohomeService(apiKey);
            setError(undefined);
        } else {
            setError(result.error);
        }
    };

    const deleteIgloohomeConfiguration = async () => {
        props.setIsLoading(true);
        let result = await deleteIgloohomePropertyConfiguration(props.propertyId);
        if (result.success) {
            props.update(undefined);
        }
        props.reload();
        props.setIsLoading(false);
    };

    return (
        <div className="access-integration-buttons">
            {configExists ? (
                <>
                    <Button
                        text="Manage Locks"
                        onClick={async () => {
                            setIsOpen(true);
                            setApiKey(
                                props.igloohomeConfiguration
                                    ? props.igloohomeConfiguration.apiKey
                                    : ''
                            );
                            await loadLocksFromIgloohomeService(
                                props.igloohomeConfiguration!.apiKey
                            );
                        }}
                        
                    />
                    <Button
                        text={'Deactivate Igloohome'}
                        addlClassName="btn-danger integration-second-button"
                        onClick={deleteIgloohomeConfiguration}
                        
                    />
                </>
            ) : (
                <div>
                    <Button
                        text={'Add IgloohomeConfiguration'}
                        onClick={() => {
                            setIsOpen(true);
                            setApiKey('');
                        }}
                        disabled={isUser}
                    />
                </div>
            )}
            <Modal size="lg" isOpen={isOpen} toggle={closePopup}>
                <LoadingOverlay show={loading} inBootstrapColumn={false} />
                <ModalBody>
                    <Container>
                        {error && apiKey && <FormValidationDisplay errorMessage={error} />}
                        {configExists ? (
                            <div>
                                <Row>
                                    <Col md={12}>
                                        {apiKey && (
                                            <div>
                                                <p>
                                                    <b>API Key: </b>
                                                    {props.igloohomeConfiguration &&
                                                        props.igloohomeConfiguration.apiKey}
                                                </p>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                {locksFromIgloohomeConfiguration.length !== 0 && (
                                    <Row>
                                        <Col md={3}>
                                            <div>
                                                <b>Lock Name</b>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div>
                                                <b>Display Name</b>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div>
                                                <b>Lock ID</b>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col md={12}>
                                        <div>
                                            {locksFromIgloohomeConfiguration &&
                                                locksFromIgloohomeConfiguration.map(iglooLock => (
                                                    <IgloohomeLockRow
                                                        key={iglooLock.lockUniqueId}
                                                        lock={iglooLock}
                                                        deleteLock={handleLockDelete}
                                                    />
                                                ))}
                                            {locksFromIgloohomeService && (
                                                <IgloohomeSelectLockRow
                                                    addLock={handleLockAdd}
                                                    availableLocksFromIgloohomeService={
                                                        locksFromIgloohomeConfiguration
                                                            ? locksFromIgloohomeService.filter(
                                                                lock =>
                                                                    !locksFromIgloohomeConfiguration.some(
                                                                        configLock =>
                                                                            configLock.lockUniqueId ===
                                                                            lock.id
                                                                    )
                                                            )
                                                            : locksFromIgloohomeService
                                                    }
                                                />
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <FormInput
                                onChange={(_name, value) => setApiKey(value.toString())}
                                labelProps={{ labelText: 'API Key' }}
                                inputProps={{
                                    id: 'api-key',
                                    name: 'apiKey',
                                    value: apiKey
                                }}
                                helpTextProps={{
                                    helpText:
                                        `Please provide the API key for your Igloohome account. ` +
                                        `If you don't have one yet, you'll need to create ` +
                                        `an Igloohome account and work with Igloohome support ` +
                                        `(support@igloohome.co) to generate one.`
                                }}
                            />
                        )}

                        <Row>
                            <Col>
                                <Button
                                    onClick={closePopup}
                                    text="Close"
                                    addlClassName="btn-secondary"
                                />
                                {!configExists && (
                                    <Button
                                        onClick={addIgloohomeConfiguration}
                                        text="Set Configuration"
                                        disabled={!apiKey}
                                    />
                                )}
                            </Col>
                        </Row>
                    </Container>
                </ModalBody>
            </Modal>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/integrationControls/IgloohomeLocksModal.tsx